import { useState } from 'react';
import {
  Button,
  Checkbox,
  Drawer,
  Empty,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, DiffOutlined, StarFilled, StarOutlined } from '@ant-design/icons';
import { MethodBadge } from '@presentation/components/workbench/MethodBadge';
import { ResponseViewer } from '@presentation/components/workbench/ResponseViewer';
import { ResponseDiffModal } from '@presentation/components/workbench/ResponseDiffModal';
import {
  useClearHistory,
  useDeleteHistoryEntry,
  useHistory,
  useToggleHistoryFavorite,
} from '@presentation/hooks/useHistory';
import type { HistoryEntry } from '@core/entities/History';

interface HistoryPanelProps {
  workspaceId: string;
  open: boolean;
  onClose: () => void;
}

export const HistoryPanel = ({ workspaceId, open, onClose }: HistoryPanelProps) => {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<HistoryEntry | null>(null);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [diffOpen, setDiffOpen] = useState(false);
  const { data, isLoading } = useHistory(workspaceId, { search: search || undefined });
  const deleteEntry = useDeleteHistoryEntry(workspaceId);
  const clearHistory = useClearHistory(workspaceId);
  const toggleFavorite = useToggleHistoryFavorite(workspaceId);

  const toggleCompareSelection = (id: string) => {
    setCompareIds((current) => {
      if (current.includes(id)) return current.filter((existing) => existing !== id);
      if (current.length >= 2) return [current[1], id];
      return [...current, id];
    });
  };

  const compareLeft = data?.data.find((entry) => entry.id === compareIds[0]) ?? null;
  const compareRight = data?.data.find((entry) => entry.id === compareIds[1]) ?? null;

  return (
    <Drawer
      title="History"
      open={open}
      onClose={onClose}
      size={420}
      extra={
        <div className="flex gap-2">
          <Button
            size="small"
            type={compareMode ? 'primary' : 'default'}
            icon={<DiffOutlined />}
            onClick={() => {
              setCompareMode((value) => !value);
              setCompareIds([]);
            }}
          >
            Compare
          </Button>
          <Popconfirm title="Clear all history?" onConfirm={() => clearHistory.mutate()}>
            <Button size="small" danger>
              Clear all
            </Button>
          </Popconfirm>
        </div>
      }
    >
      <Input.Search
        placeholder="Search history"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        allowClear
        className="mb-3"
      />

      {compareMode && (
        <div className="mb-3 flex items-center justify-between rounded border border-black/10 px-2 py-1.5 text-xs dark:border-white/10">
          <span className="text-gray-500">
            Select two requests to compare ({compareIds.length}/2)
          </span>
          <Button
            size="small"
            type="primary"
            disabled={compareIds.length !== 2}
            onClick={() => setDiffOpen(true)}
          >
            Compare
          </Button>
        </div>
      )}

      {isLoading && <Spin size="small" />}

      {!isLoading && data?.data.length === 0 && <Empty description="No requests sent yet" />}

      <div className="flex flex-col divide-y divide-black/5 dark:divide-white/10">
        {data?.data.map((entry) => (
          <div
            key={entry.id}
            role="button"
            tabIndex={0}
            onClick={() => (compareMode ? toggleCompareSelection(entry.id) : setSelected(entry))}
            className="flex cursor-pointer items-center gap-2 py-2"
          >
            {compareMode && (
              <Checkbox
                checked={compareIds.includes(entry.id)}
                onClick={(event) => event.stopPropagation()}
                onChange={() => toggleCompareSelection(entry.id)}
              />
            )}
            <MethodBadge method={entry.method} className="w-10 shrink-0" />
            <div className="min-w-0 flex-1">
              <Typography.Text className="block truncate text-xs">{entry.url}</Typography.Text>
              <Typography.Text type="secondary" className="text-[11px]">
                {new Date(entry.executedAt).toLocaleString()}
              </Typography.Text>
            </div>
            {entry.errorMessage ? (
              <Tag color="error">Failed</Tag>
            ) : (
              <Tag color={entry.statusCode && entry.statusCode < 400 ? 'success' : 'error'}>
                {entry.statusCode}
              </Tag>
            )}
            <Button
              type="text"
              size="small"
              icon={
                entry.isFavorited ? <StarFilled className="text-amber-400" /> : <StarOutlined />
              }
              onClick={(event) => {
                event.stopPropagation();
                toggleFavorite.mutate({ id: entry.id, isFavorited: entry.isFavorited });
              }}
            />
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={(event) => {
                event.stopPropagation();
                deleteEntry.mutate(entry.id);
              }}
            />
          </div>
        ))}
      </div>

      <Modal
        open={Boolean(selected)}
        onCancel={() => setSelected(null)}
        footer={null}
        width={720}
        title={selected?.name ?? selected?.url}
        destroyOnHidden
      >
        <div className="h-96">
          {selected && <ResponseViewer entry={selected} isSending={false} />}
        </div>
      </Modal>

      <ResponseDiffModal
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        left={compareLeft}
        right={compareRight}
      />
    </Drawer>
  );
};
